/**
 * Peer Connection Service
 * Handles WebRTC peer-to-peer connections between AtelierCode machines.
 * Uses the signaling server for offer/answer exchange and COTURN for NAT traversal.
 */

import { serverConnection } from './serverConnection';
import { useSettingsStore } from '../stores/settingsStore';
import { useMachineStore } from '../stores/machineStore';
import { handleWebRTCCommand, isWebRTCHostAvailable } from './webrtcHost';
import { logger } from '@/lib/logger';

interface ICEServer {
  urls: string;
  username?: string;
  credential?: string;
}

interface PeerMessage {
  type: 'request' | 'response' | 'event' | 'chunk' | 'ping' | 'pong';
  id?: string;
  command?: string;
  params?: Record<string, unknown>;
  success?: boolean;
  data?: unknown;
  error?: string;
  event?: string;
  payload?: unknown;
  ts?: number;
  // Chunking fields
  chunkIndex?: number;
  totalChunks?: number;
  chunkData?: string;
}

interface HostPeer {
  pc: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  connectionId: string;
  targetMachineId: string;
  pendingChunks: Map<string, { chunks: string[]; totalChunks: number }>;
  iceDisconnectedTimer: ReturnType<typeof setTimeout> | null;
  iceRestartAttempts: number;
}

// Maximum chunk size (16KB to be safe across browsers)
const MAX_CHUNK_SIZE = 16 * 1024;

type PeerEventHandler = (event: string, payload: unknown) => void;
type DisconnectHandler = () => void;
type RemoteTrackHandler = (stream: MediaStream) => void;
type RdpInputHandler = (message: Record<string, unknown>) => void;

class PeerConnection {
  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private connectionId: string | null = null;
  private targetMachineId: string | null = null;
  private iceServers: ICEServer[] = [];
  private pendingRequests = new Map<string, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
  private eventHandlers: PeerEventHandler[] = [];
  private disconnectHandlers: DisconnectHandler[] = [];
  private remoteTrackHandlers: RemoteTrackHandler[] = [];

  // RDP input channel: dedicated fire-and-forget channel for mouse/keyboard
  private rdpInputChannel: RTCDataChannel | null = null;
  private rdpInputHandlers: RdpInputHandler[] = [];
  private isInitiator = false;
  // Track incoming chunked messages
  private pendingChunks = new Map<string, { chunks: string[]; totalChunks: number }>();

  // Multi-peer host mode: multiple web clients can connect simultaneously
  private hostPeers = new Map<string, HostPeer>();  // connectionId -> HostPeer
  private hostSignalingSubscribed = false;  // Subscribe to signaling only once

  // Auto-reconnect state
  private intentionalDisconnect = false;
  private lastConnectedMachineId: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000; // 1s, 2s, 4s, 8s, 16s, 30s, 30s...
  private maxReconnectDelay = 30000;

  // ICE restart state (lightweight recovery before full teardown)
  private iceDisconnectedTimer: ReturnType<typeof setTimeout> | null = null;
  private iceRestartAttempts = 0;
  private maxIceRestartAttempts = 3;
  private iceDisconnectedTimeout = 5000; // 5s before escalating

  // Data channel keepalive
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  private keepaliveTimeout: ReturnType<typeof setTimeout> | null = null;
  private keepaliveIntervalMs = 15000; // ping every 15s
  private keepaliveTimeoutMs = 10000;  // expect pong within 10s

  // Host peer cleanup
  private hostPeerCleanupInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Fetch ICE server configuration from the signaling server
   */
  async fetchIceServers(): Promise<ICEServer[]> {
    const settings = useSettingsStore.getState();
    if (!settings.server.url) {
      logger.warn('PeerConnection', 'No server URL configured');
      return this.getDefaultIceServers();
    }

    try {
      // Convert WebSocket URL to HTTP URL for the ICE servers endpoint
      const httpUrl = settings.server.url
        .replace('wss://', 'https://')
        .replace('ws://', 'http://');

      // /ice-servers is auth-gated (it hands out TURN credentials). Send the
      // same bearer token the signaling connection uses — without it the server
      // 401s and we silently fall back to STUN-only, so WebRTC can't traverse
      // symmetric NAT and cross-network remote sessions fail to connect.
      const { useAuthStore } = await import('../stores/authStore');
      const token = (await useAuthStore.getState().ensureFreshToken()) || settings.server.token;
      const response = await fetch(
        `${httpUrl}/ice-servers`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch ICE servers: ${response.status}`);
      }

      const data = await response.json();
      this.iceServers = data.iceServers || [];
      logger.debug('PeerConnection', 'Fetched ICE servers:', this.iceServers.length);
      return this.iceServers;
    } catch (error) {
      logger.warn('PeerConnection', 'Failed to fetch ICE servers, using defaults:', error);
      return this.getDefaultIceServers();
    }
  }

  /**
   * Default ICE servers (STUN only - for development/fallback)
   */
  private getDefaultIceServers(): ICEServer[] {
    return [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];
  }

  /**
   * Initialize a connection to a remote machine (as initiator)
   */
  async connect(targetMachineId: string): Promise<boolean> {
    if (this.pc) {
      logger.warn('PeerConnection', 'Already connected or connecting');
      return false;
    }

    this.intentionalDisconnect = false;
    this.lastConnectedMachineId = targetMachineId;
    this.targetMachineId = targetMachineId;
    this.isInitiator = true;

    // Fetch ICE servers
    const iceServers = await this.fetchIceServers();

    // Create peer connection
    this.pc = new RTCPeerConnection({
      iceServers: iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      })),
    });

    this.setupPeerConnectionHandlers();

    // Create data channel (initiator creates it)
    this.dataChannel = this.pc.createDataChannel('ateliercode', {
      ordered: true,
    });
    this.setupDataChannelHandlers();

    // Listen for signaling messages
    const unsubscribe = serverConnection.onMessage((message) => {
      this.handleSignalingMessage(message);
    });

    // Request connection via signaling server
    const connected = await serverConnection.connectToMachine(targetMachineId);
    if (!connected) {
      this.cleanup();
      unsubscribe();
      return false;
    }

    // Wait for connection to establish (with timeout)
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        if (this.dataChannel?.readyState !== 'open') {
          logger.debug('PeerConnection', 'Connection timeout');
          this.cleanup();
          unsubscribe();
          resolve(false);
        }
      }, 30000);

      const checkConnection = () => {
        if (this.dataChannel?.readyState === 'open') {
          clearTimeout(timeout);
          unsubscribe();
          resolve(true);
        }
      };

      // Check periodically
      const interval = setInterval(() => {
        if (this.dataChannel?.readyState === 'open') {
          clearInterval(interval);
          checkConnection();
        } else if (!this.pc) {
          clearInterval(interval);
          clearTimeout(timeout);
          unsubscribe();
          resolve(false);
        }
      }, 100);
    });
  }

  /**
   * Accept an incoming connection (as responder / host)
   * Supports multiple simultaneous web clients — each gets its own HostPeer.
   */
  async acceptConnection(connectionId: string, fromMachineId: string): Promise<void> {
    logger.debug('PeerConnection', `Accepting connection as host, connectionId: ${connectionId}, from: ${fromMachineId} (existing peers: ${this.hostPeers.size})`);

    // Fetch ICE servers
    const iceServers = await this.fetchIceServers();

    // Create a new RTCPeerConnection for this client
    const pc = new RTCPeerConnection({
      iceServers: iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      })),
    });

    const peer: HostPeer = {
      pc,
      dataChannel: null,
      connectionId,
      targetMachineId: fromMachineId,
      pendingChunks: new Map(),
      iceDisconnectedTimer: null,
      iceRestartAttempts: 0,
    };

    this.hostPeers.set(connectionId, peer);
    this.setupHostPeerHandlers(peer);

    // Start periodic cleanup of stale host peers
    if (!this.hostPeerCleanupInterval) {
      this.hostPeerCleanupInterval = setInterval(() => {
        for (const [connId, p] of this.hostPeers) {
          const state = p.pc.iceConnectionState;
          if (state === 'failed' || state === 'closed') {
            logger.debug('PeerConnection', `Cleaning stale host peer ${connId} (${state})`);
            this.cleanupHostPeer(connId);
          }
        }
        if (this.hostPeers.size === 0 && this.hostPeerCleanupInterval) {
          clearInterval(this.hostPeerCleanupInterval);
          this.hostPeerCleanupInterval = null;
        }
      }, 30000);
    }

    // Listen for data channel (responder receives it)
    pc.ondatachannel = (event) => {
      logger.debug('PeerConnection', `Data channel received from initiator (conn: ${connectionId})`);
      peer.dataChannel = event.channel;
      this.setupHostDataChannelHandlers(peer);
    };

    // Subscribe to signaling messages only once across all host peers
    if (!this.hostSignalingSubscribed) {
      this.hostSignalingSubscribed = true;
      serverConnection.onMessage((message) => {
        this.handleSignalingMessage(message);
      });
    }
  }

  /**
   * Send a message through a specific data channel, chunking if necessary
   */
  private async sendChunkedMessageToChannel(channel: RTCDataChannel, message: PeerMessage): Promise<void> {
    const messageStr = JSON.stringify(message);

    // If message fits in one chunk, send directly
    if (messageStr.length <= MAX_CHUNK_SIZE) {
      channel.send(messageStr);
      return;
    }

    // Split into chunks
    const totalChunks = Math.ceil(messageStr.length / MAX_CHUNK_SIZE);
    logger.debug('PeerConnection', `Sending large message in ${totalChunks} chunks (${messageStr.length} bytes)`);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * MAX_CHUNK_SIZE;
      const end = Math.min(start + MAX_CHUNK_SIZE, messageStr.length);
      const chunkData = messageStr.slice(start, end);

      const chunkMessage: PeerMessage = {
        type: 'chunk',
        id: message.id,
        chunkIndex: i,
        totalChunks,
        chunkData,
      };

      // Wait for buffer to drain if needed (prevent overflow)
      while (channel.bufferedAmount > 65536) {
        logger.debug('PeerConnection', `Waiting for buffer to drain (${channel.bufferedAmount} bytes buffered)`);
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      channel.send(JSON.stringify(chunkMessage));

      // Log progress for large transfers
      if (totalChunks > 10 && (i + 1) % 10 === 0) {
        logger.debug('PeerConnection', `Sent chunk ${i + 1}/${totalChunks}`);
      }
    }

    logger.debug('PeerConnection', `All ${totalChunks} chunks sent`);
  }

  /**
   * Send a message via the client-mode data channel, chunking if necessary
   */
  private async sendChunkedMessage(message: PeerMessage): Promise<void> {
    return this.sendChunkedMessageToChannel(this.dataChannel!, message);
  }

  /**
   * Send a command to the remote machine and wait for response
   */
  async sendCommand<T>(command: string, params: Record<string, unknown> = {}): Promise<T> {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('Not connected to remote machine');
    }

    const id = crypto.randomUUID();

    const message: PeerMessage = {
      type: 'request',
      id,
      command,
      params,
    };

    // Send the message (await chunked sending if needed)
    await this.sendChunkedMessage(message);

    // Now wait for response
    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve: resolve as (v: unknown) => void, reject });

      // Timeout after 120 seconds (longer for large payloads like audio transcription)
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Request timeout'));
        }
      }, 120000);
    });
  }

  /**
   * Subscribe to events from the remote machine
   */
  onEvent(handler: PeerEventHandler): () => void {
    this.eventHandlers.push(handler);
    return () => {
      const index = this.eventHandlers.indexOf(handler);
      if (index >= 0) {
        this.eventHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Send an event to all connected peers (broadcasts to host peers + client channel)
   */
  sendEvent(eventName: string, payload: unknown): void {
    const message: PeerMessage = { type: 'event', event: eventName, payload };
    const messageStr = JSON.stringify(message);

    // Broadcast to all host peers
    for (const [connId, peer] of this.hostPeers) {
      if (peer.dataChannel?.readyState === 'open') {
        try {
          peer.dataChannel.send(messageStr);
        } catch (error) {
          logger.error('PeerConnection', `Failed to send event to peer ${connId}:`, error);
        }
      }
    }

    // Also send to single client-mode channel (for backward compat / client use)
    if (this.dataChannel?.readyState === 'open') {
      try {
        this.dataChannel.send(messageStr);
      } catch (error) {
        logger.error('PeerConnection', 'Failed to send event:', error);
      }
    }

    if (this.hostPeers.size === 0 && this.dataChannel?.readyState !== 'open') {
      logger.warn('PeerConnection', 'Cannot send event - no connected peers');
    }
  }

  // ── RDP: Video track management ──────────────────────────────────────

  /**
   * Add a video track (screen capture) to all connected host peers.
   * Triggers SDP renegotiation automatically via onnegotiationneeded.
   */
  addVideoTrackToHost(track: MediaStreamTrack, stream: MediaStream): void {
    for (const [connId, peer] of this.hostPeers) {
      if (peer.pc.connectionState !== 'closed') {
        logger.info('PeerConnection', `Adding video track to host peer ${connId}`);
        peer.pc.addTrack(track, stream);
      }
    }
  }

  /**
   * Configure video encoding parameters on host peers:
   * - Prefer H.264 codec
   * - Max bitrate 5Mbps
   * - scaleResolutionDownBy for lower bandwidth
   */
  configureVideoEncoding(): void {
    for (const [connId, peer] of this.hostPeers) {
      const senders = peer.pc.getSenders().filter((s) => s.track?.kind === 'video');
      for (const sender of senders) {
        // Set codec preference to H.264 if supported
        const transceiver = peer.pc.getTransceivers().find((t) => t.sender === sender);
        if (transceiver && typeof transceiver.setCodecPreferences === 'function') {
          const codecs = RTCRtpReceiver.getCapabilities?.('video')?.codecs || [];
          const h264 = codecs.filter((c) => c.mimeType === 'video/H264');
          const others = codecs.filter((c) => c.mimeType !== 'video/H264');
          if (h264.length > 0) {
            try {
              transceiver.setCodecPreferences([...h264, ...others]);
              logger.info('PeerConnection', `Set H.264 codec preference for peer ${connId}`);
            } catch (err) {
              logger.warn('PeerConnection', 'Failed to set codec preferences:', err);
            }
          }
        }

        // Set max bitrate to 5Mbps
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        params.encodings[0].maxBitrate = 5_000_000; // 5 Mbps
        sender.setParameters(params).catch((err) => {
          logger.warn('PeerConnection', 'Failed to set encoding params:', err);
        });
      }
    }
  }

  /**
   * Remove all video tracks from host peers (stops RDP streaming).
   */
  removeVideoTrackFromHost(): void {
    for (const [connId, peer] of this.hostPeers) {
      const senders = peer.pc.getSenders().filter((s) => s.track?.kind === 'video');
      for (const sender of senders) {
        logger.info('PeerConnection', `Removing video track from host peer ${connId}`);
        peer.pc.removeTrack(sender);
      }
    }
  }

  /**
   * Subscribe to receive remote video tracks (used by RDP viewer on client side).
   */
  onRemoteTrack(handler: RemoteTrackHandler): () => void {
    this.remoteTrackHandlers.push(handler);
    return () => {
      const index = this.remoteTrackHandlers.indexOf(handler);
      if (index >= 0) this.remoteTrackHandlers.splice(index, 1);
    };
  }

  // ── RDP Input channel (fire-and-forget for mouse/keyboard) ──────────

  /**
   * Create the rdp-input data channel on all host peers (called when RDP starts).
   * Ordered and reliable for correct input sequencing.
   */
  createRdpInputChannels(): void {
    for (const [connId, peer] of this.hostPeers) {
      if (peer.pc.connectionState !== 'closed') {
        const channel = peer.pc.createDataChannel('rdp-input', { ordered: true });
        channel.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            for (const handler of this.rdpInputHandlers) {
              handler(msg);
            }
          } catch (err) {
            logger.warn('PeerConnection', `Invalid rdp-input message from ${connId}:`, err);
          }
        };
        logger.info('PeerConnection', `Created rdp-input channel for peer ${connId}`);
      }
    }
  }

  /**
   * Send a fire-and-forget input message on the rdp-input channel (client side).
   * Does not wait for a response — optimized for low-latency input.
   */
  sendRdpInput(message: Record<string, unknown>): void {
    if (this.rdpInputChannel?.readyState === 'open') {
      this.rdpInputChannel.send(JSON.stringify(message));
    }
  }

  /**
   * Subscribe to incoming rdp-input messages (host side).
   */
  onRdpInput(handler: RdpInputHandler): () => void {
    this.rdpInputHandlers.push(handler);
    return () => {
      const index = this.rdpInputHandlers.indexOf(handler);
      if (index >= 0) this.rdpInputHandlers.splice(index, 1);
    };
  }

  /**
   * Get WebRTC stats from the client-side peer connection.
   * Returns bitrate (bps) and latency (ms) from RTCPeerConnection.getStats().
   */
  async getRtcStats(): Promise<{ bitrate: number; latency: number } | null> {
    if (!this.pc) return null;
    try {
      const stats = await this.pc.getStats();
      let bytesReceived = 0;
      let timestamp = 0;
      let latency = 0;

      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          bytesReceived = report.bytesReceived || 0;
          timestamp = report.timestamp || 0;
        }
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          latency = Math.round((report.currentRoundTripTime || 0) * 1000);
        }
      });

      // Store for delta calculation
      const prevBytes = (this as Record<string, unknown>)._prevBytesReceived as number || 0;
      const prevTs = (this as Record<string, unknown>)._prevStatsTimestamp as number || 0;
      (this as Record<string, unknown>)._prevBytesReceived = bytesReceived;
      (this as Record<string, unknown>)._prevStatsTimestamp = timestamp;

      let bitrate = 0;
      if (prevTs > 0 && timestamp > prevTs) {
        const deltaBytes = bytesReceived - prevBytes;
        const deltaSec = (timestamp - prevTs) / 1000;
        bitrate = Math.round((deltaBytes * 8) / deltaSec); // bits per second
      }

      return { bitrate, latency };
    } catch {
      return null;
    }
  }

  /**
   * Subscribe to disconnect events
   */
  onDisconnect(handler: DisconnectHandler): () => void {
    this.disconnectHandlers.push(handler);
    return () => {
      const index = this.disconnectHandlers.indexOf(handler);
      if (index >= 0) {
        this.disconnectHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Disconnect from the remote machine (intentional)
   */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.lastConnectedMachineId = null;
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.cleanup();
  }

  /**
   * Non-intentional cleanup — tears down the current connection but preserves
   * lastConnectedMachineId so auto-reconnect can pick it back up.
   * Used when the server reports our machine went offline due to a stale WS.
   */
  teardownStaleConnection(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.cleanup();
  }

  /**
   * Clean up all host peers (used after WS reconnect when signaling context is gone).
   * Stale host peers hold RTCPeerConnections whose signaling path no longer exists
   * because the old WebSocket died — they can never recover, so close them.
   */
  cleanupStaleHostPeers(): void {
    if (this.hostPeers.size > 0) {
      logger.info('PeerConnection', `Cleaning up ${this.hostPeers.size} stale host peers after WS reconnect`);
      for (const [connId] of this.hostPeers) {
        this.cleanupHostPeer(connId);
      }
    }
  }

  /**
   * Check if connected (either as client or hosting any peers)
   */
  get isConnected(): boolean {
    return this.dataChannel?.readyState === 'open' || this.hasHostPeers;
  }

  /**
   * Check if any host peers have open data channels
   */
  private get hasHostPeers(): boolean {
    for (const peer of this.hostPeers.values()) {
      if (peer.dataChannel?.readyState === 'open') return true;
    }
    return false;
  }

  /**
   * Check if this machine is the host (responder) of the connection
   * Hosts receive commands from clients and execute them locally
   */
  get isHost(): boolean {
    return this.hasHostPeers;
  }

  /**
   * Check if this machine is the client (initiator) of the connection
   * Clients send commands to the host for execution
   */
  get isClient(): boolean {
    return this.dataChannel?.readyState === 'open' && this.isInitiator;
  }

  private setupPeerConnectionHandlers(): void {
    if (!this.pc) return;

    this.pc.onicecandidate = (event) => {
      if (event.candidate && this.targetMachineId && this.connectionId) {
        // Send ICE candidate to remote peer via signaling server
        serverConnection.send({
          type: 'rtc_ice_candidate',
          payload: {
            connectionId: this.connectionId,
            targetMachineId: this.targetMachineId,
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
          },
        });
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      const state = this.pc?.iceConnectionState;
      logger.debug('PeerConnection', 'ICE connection state:', state);

      switch (state) {
        case 'disconnected':
          // Start 5s timer — if still disconnected, try ICE restart
          if (!this.iceDisconnectedTimer) {
            this.iceDisconnectedTimer = setTimeout(() => {
              this.iceDisconnectedTimer = null;
              if (this.pc?.iceConnectionState === 'disconnected') {
                logger.warn('PeerConnection', 'ICE still disconnected after 5s, attempting ICE restart');
                this.performIceRestart();
              }
            }, this.iceDisconnectedTimeout);
          }
          break;

        case 'failed':
          this.clearIceDisconnectedTimer();
          if (this.iceRestartAttempts < this.maxIceRestartAttempts) {
            this.performIceRestart();
          } else {
            logger.warn('PeerConnection', 'ICE restart attempts exhausted, full reconnect');
            this.iceRestartAttempts = 0;
            this.handleUnexpectedDisconnect();
          }
          break;

        case 'connected':
        case 'completed':
          this.clearIceDisconnectedTimer();
          this.iceRestartAttempts = 0;
          break;

        case 'closed':
          this.clearIceDisconnectedTimer();
          break;
      }
    };

    this.pc.onconnectionstatechange = () => {
      logger.debug('PeerConnection', 'Connection state:', this.pc?.connectionState);
    };

    // Handle incoming video tracks (RDP stream from host)
    this.pc.ontrack = (event) => {
      logger.info('PeerConnection', `Received remote track: kind=${event.track.kind}`);
      if (event.track.kind === 'video' && event.streams[0]) {
        for (const handler of this.remoteTrackHandlers) {
          handler(event.streams[0]);
        }
      }
    };

    // Handle incoming data channels (client receives rdp-input channel from host)
    this.pc.ondatachannel = (event) => {
      if (event.channel.label === 'rdp-input') {
        logger.info('PeerConnection', 'Received rdp-input data channel from host');
        this.rdpInputChannel = event.channel;
      }
    };
  }

  private setupDataChannelHandlers(): void {
    if (!this.dataChannel) return;

    this.dataChannel.onopen = () => {
      logger.debug('PeerConnection', 'Data channel opened');
      this.reconnectAttempts = 0; // Reset on successful connection
      this.startKeepalive();
    };

    this.dataChannel.onclose = () => {
      logger.debug('PeerConnection', 'Data channel closed');
      this.handleUnexpectedDisconnect();
    };

    this.dataChannel.onerror = (error) => {
      logger.error('PeerConnection', 'Data channel error:', error);
    };

    this.dataChannel.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PeerMessage;
        this.handlePeerMessage(message);
      } catch (error) {
        logger.error('PeerConnection', 'Failed to parse message:', error);
      }
    };
  }

  private handlePeerMessage(message: PeerMessage): void {
    switch (message.type) {
      case 'ping':
        if (this.dataChannel?.readyState === 'open') {
          this.dataChannel.send(JSON.stringify({ type: 'pong', ts: message.ts }));
        }
        return;

      case 'pong':
        if (this.keepaliveTimeout) {
          clearTimeout(this.keepaliveTimeout);
          this.keepaliveTimeout = null;
        }
        return;

      case 'chunk':
        // Handle chunked message reassembly
        this.handleChunk(message);
        break;

      case 'response':
        // Handle response to pending request
        if (message.id && this.pendingRequests.has(message.id)) {
          const { resolve, reject } = this.pendingRequests.get(message.id)!;
          this.pendingRequests.delete(message.id);
          if (message.success) {
            resolve(message.data);
          } else {
            reject(new Error(message.error || 'Unknown error'));
          }
        }
        break;

      case 'event':
        // Notify event handlers
        if (message.event) {
          for (const handler of this.eventHandlers) {
            handler(message.event, message.payload);
          }
        }
        break;

      case 'request':
        // Handle incoming request (for when this machine is the host/remote)
        if (isWebRTCHostAvailable()) {
          logger.debug('PeerConnection', 'Received request:', message.command);
          // Execute the command and send response
          // Cast to strip 'chunk' type since we're in the 'request' branch
          handleWebRTCCommand(message as Parameters<typeof handleWebRTCCommand>[0]).then(async (response) => {
            if (this.dataChannel && this.dataChannel.readyState === 'open') {
              // Response might also be large, so use chunked sending
              await this.sendChunkedMessage(response);
            } else {
              logger.warn('PeerConnection', `Cannot send response for ${message.command}: data channel not open`);
            }
          }).catch((error) => {
            logger.error('PeerConnection', `Failed to handle command ${message.command}:`, error);
            // Send error response so client doesn't hang waiting for a timeout
            if (this.dataChannel && this.dataChannel.readyState === 'open') {
              this.dataChannel.send(JSON.stringify({
                type: 'response',
                id: message.id,
                success: false,
                error: `Host error: ${error instanceof Error ? error.message : 'Unknown error'}`,
              }));
            }
          });
        } else {
          logger.warn('PeerConnection', 'Received request but not running as host');
          // Send error response
          if (this.dataChannel && this.dataChannel.readyState === 'open') {
            this.dataChannel.send(JSON.stringify({
              type: 'response',
              id: message.id,
              success: false,
              error: 'This machine is not running as a host',
            }));
          }
        }
        break;
    }
  }

  /**
   * Handle incoming chunk and reassemble when complete
   */
  private handleChunk(chunk: PeerMessage): void {
    if (!chunk.id || chunk.chunkIndex === undefined || !chunk.totalChunks || !chunk.chunkData) {
      logger.error('PeerConnection', 'Invalid chunk message:', {
        hasId: !!chunk.id,
        chunkIndex: chunk.chunkIndex,
        totalChunks: chunk.totalChunks,
        hasChunkData: !!chunk.chunkData,
      });
      return;
    }

    // Get or create pending chunk assembly
    let pending = this.pendingChunks.get(chunk.id);
    if (!pending) {
      logger.debug('PeerConnection', `Starting to receive chunked message: ${chunk.totalChunks} chunks expected`);
      pending = { chunks: new Array(chunk.totalChunks).fill(null), totalChunks: chunk.totalChunks };
      this.pendingChunks.set(chunk.id, pending);

      // Timeout: clean up incomplete chunks after 60s
      const chunkId = chunk.id;
      setTimeout(() => {
        if (this.pendingChunks.has(chunkId)) {
          logger.warn('PeerConnection', `Chunk reassembly timeout for ${chunkId}, discarding`);
          this.pendingChunks.delete(chunkId);
        }
      }, 60000);
    }

    // Store chunk data
    pending.chunks[chunk.chunkIndex] = chunk.chunkData;

    // Check if all chunks received
    const receivedCount = pending.chunks.filter(c => c !== null).length;

    // Log progress for large transfers
    if (pending.totalChunks > 10 && receivedCount % 10 === 0) {
      logger.debug('PeerConnection', `Received chunk ${receivedCount}/${pending.totalChunks}`);
    }

    if (receivedCount === pending.totalChunks) {
      // Reassemble and process
      const fullMessage = pending.chunks.join('');
      this.pendingChunks.delete(chunk.id);

      logger.debug('PeerConnection', `All chunks received! Reassembled message: ${fullMessage.length} bytes`);

      try {
        const reassembledMessage = JSON.parse(fullMessage) as PeerMessage;
        // Process the reassembled message
        this.handlePeerMessage(reassembledMessage);
      } catch (error) {
        logger.error('PeerConnection', 'Failed to parse reassembled message:', error);
      }
    }
  }

  // ── Multi-peer host mode methods ──────────────────────────────────────

  /**
   * Set up ICE and connection handlers for a host peer
   */
  private setupHostPeerHandlers(peer: HostPeer): void {
    peer.pc.onicecandidate = (event) => {
      if (event.candidate) {
        serverConnection.send({
          type: 'rtc_ice_candidate',
          payload: {
            connectionId: peer.connectionId,
            targetMachineId: peer.targetMachineId,
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
          },
        });
      }
    };

    peer.pc.oniceconnectionstatechange = () => {
      const state = peer.pc.iceConnectionState;
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} ICE state: ${state}`);

      switch (state) {
        case 'disconnected':
          if (!peer.iceDisconnectedTimer) {
            peer.iceDisconnectedTimer = setTimeout(() => {
              peer.iceDisconnectedTimer = null;
              if (peer.pc.iceConnectionState === 'disconnected') {
                logger.warn('PeerConnection', `Host peer ${peer.connectionId} still disconnected after 5s, ICE restart`);
                this.performHostPeerIceRestart(peer);
              }
            }, this.iceDisconnectedTimeout);
          }
          break;

        case 'failed':
          if (peer.iceDisconnectedTimer) { clearTimeout(peer.iceDisconnectedTimer); peer.iceDisconnectedTimer = null; }
          if (peer.iceRestartAttempts < this.maxIceRestartAttempts) {
            this.performHostPeerIceRestart(peer);
          } else {
            logger.warn('PeerConnection', `Host peer ${peer.connectionId} ICE restart exhausted, cleaning up`);
            peer.iceRestartAttempts = 0;
            this.cleanupHostPeer(peer.connectionId);
          }
          break;

        case 'connected':
        case 'completed':
          if (peer.iceDisconnectedTimer) { clearTimeout(peer.iceDisconnectedTimer); peer.iceDisconnectedTimer = null; }
          peer.iceRestartAttempts = 0;
          break;

        case 'closed':
          if (peer.iceDisconnectedTimer) { clearTimeout(peer.iceDisconnectedTimer); peer.iceDisconnectedTimer = null; }
          break;
      }
    };

    peer.pc.onconnectionstatechange = () => {
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} connection state: ${peer.pc.connectionState}`);
    };

    // Renegotiation: when video track is added/removed, create new offer
    peer.pc.onnegotiationneeded = async () => {
      logger.info('PeerConnection', `Host peer ${peer.connectionId} negotiation needed (renegotiating)`);
      try {
        const offer = await peer.pc.createOffer();
        await peer.pc.setLocalDescription(offer);
        serverConnection.send({
          type: 'rtc_offer',
          payload: {
            connectionId: peer.connectionId,
            targetMachineId: peer.targetMachineId,
            sdp: offer.sdp,
          },
        });
      } catch (error) {
        logger.error('PeerConnection', `Renegotiation failed for peer ${peer.connectionId}:`, error);
      }
    };
  }

  /**
   * Set up data channel handlers for a host peer
   */
  private setupHostDataChannelHandlers(peer: HostPeer): void {
    if (!peer.dataChannel) return;

    peer.dataChannel.onopen = () => {
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} data channel opened (total peers: ${this.hostPeers.size})`);
      // Arm terminal-data forwarding as soon as a client connects. Previously the
      // host only armed it lazily inside the terminal_* command handlers, so a
      // client that attached to an existing session (restored from cache, no
      // fresh terminal_create/list) never triggered it — live PTY output never
      // streamed and the remote terminal stayed blank. setupTerminalEventForwarding
      // is idempotent, so re-arming per connection is safe. Dynamic import avoids
      // the webrtcHost <-> peerConnection circular dependency at module load.
      if (isWebRTCHostAvailable()) {
        import('./webrtcHost')
          .then((m) => m.setupTerminalEventForwarding())
          .catch((e) => logger.warn('PeerConnection', 'arm terminal forwarding failed:', e));
      }
    };

    peer.dataChannel.onclose = () => {
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} data channel closed`);
      this.cleanupHostPeer(peer.connectionId);
    };

    peer.dataChannel.onerror = (error) => {
      logger.error('PeerConnection', `Host peer ${peer.connectionId} data channel error:`, error);
    };

    peer.dataChannel.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PeerMessage;
        this.handleHostPeerMessage(peer, message);
      } catch (error) {
        logger.error('PeerConnection', `Host peer ${peer.connectionId} failed to parse message:`, error);
      }
    };
  }

  /**
   * Handle a message from a specific host peer
   */
  private handleHostPeerMessage(peer: HostPeer, message: PeerMessage): void {
    switch (message.type) {
      case 'ping':
        if (peer.dataChannel?.readyState === 'open') {
          peer.dataChannel.send(JSON.stringify({ type: 'pong', ts: message.ts }));
        }
        return;

      case 'pong':
        // Host doesn't send keepalives to clients, but handle gracefully
        return;

      case 'chunk':
        this.handleHostChunk(peer, message);
        break;

      case 'request':
        if (isWebRTCHostAvailable()) {
          logger.debug('PeerConnection', `Host peer ${peer.connectionId} request: ${message.command}`);
          handleWebRTCCommand(message as Parameters<typeof handleWebRTCCommand>[0]).then(async (response) => {
            if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
              await this.sendChunkedMessageToChannel(peer.dataChannel, response);
            } else {
              logger.warn('PeerConnection', `Cannot send response for ${message.command}: peer ${peer.connectionId} channel not open`);
            }
          }).catch((error) => {
            logger.error('PeerConnection', `Host peer ${peer.connectionId} failed to handle command ${message.command}:`, error);
            if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
              peer.dataChannel.send(JSON.stringify({
                type: 'response',
                id: message.id,
                success: false,
                error: `Host error: ${error instanceof Error ? error.message : 'Unknown error'}`,
              }));
            }
          });
        } else {
          logger.warn('PeerConnection', 'Received request but not running as host');
          if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
            peer.dataChannel.send(JSON.stringify({
              type: 'response',
              id: message.id,
              success: false,
              error: 'This machine is not running as a host',
            }));
          }
        }
        break;

      case 'response':
      case 'event':
        logger.warn('PeerConnection', `Unexpected ${message.type} message on host peer ${peer.connectionId}`);
        break;
    }
  }

  /**
   * Handle incoming chunk for a specific host peer (per-peer reassembly)
   */
  private handleHostChunk(peer: HostPeer, chunk: PeerMessage): void {
    if (!chunk.id || chunk.chunkIndex === undefined || !chunk.totalChunks || !chunk.chunkData) {
      logger.error('PeerConnection', `Host peer ${peer.connectionId} invalid chunk:`, {
        hasId: !!chunk.id,
        chunkIndex: chunk.chunkIndex,
        totalChunks: chunk.totalChunks,
        hasChunkData: !!chunk.chunkData,
      });
      return;
    }

    let pending = peer.pendingChunks.get(chunk.id);
    if (!pending) {
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} starting chunked receive: ${chunk.totalChunks} chunks`);
      pending = { chunks: new Array(chunk.totalChunks).fill(null), totalChunks: chunk.totalChunks };
      peer.pendingChunks.set(chunk.id, pending);

      // Timeout: clean up incomplete chunks after 60s
      const chunkId = chunk.id;
      setTimeout(() => {
        if (peer.pendingChunks.has(chunkId)) {
          logger.warn('PeerConnection', `Host peer ${peer.connectionId} chunk timeout for ${chunkId}`);
          peer.pendingChunks.delete(chunkId);
        }
      }, 60000);
    }

    pending.chunks[chunk.chunkIndex] = chunk.chunkData;
    const receivedCount = pending.chunks.filter(c => c !== null).length;

    if (pending.totalChunks > 10 && receivedCount % 10 === 0) {
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} chunk ${receivedCount}/${pending.totalChunks}`);
    }

    if (receivedCount === pending.totalChunks) {
      const fullMessage = pending.chunks.join('');
      peer.pendingChunks.delete(chunk.id);
      logger.debug('PeerConnection', `Host peer ${peer.connectionId} reassembled: ${fullMessage.length} bytes`);

      try {
        const reassembledMessage = JSON.parse(fullMessage) as PeerMessage;
        this.handleHostPeerMessage(peer, reassembledMessage);
      } catch (error) {
        logger.error('PeerConnection', `Host peer ${peer.connectionId} failed to parse reassembled message:`, error);
      }
    }
  }

  /**
   * Clean up a single host peer
   */
  private cleanupHostPeer(connectionId: string): void {
    const peer = this.hostPeers.get(connectionId);
    if (!peer) return;

    logger.debug('PeerConnection', `Cleaning up host peer ${connectionId}`);
    if (peer.iceDisconnectedTimer) {
      clearTimeout(peer.iceDisconnectedTimer);
      peer.iceDisconnectedTimer = null;
    }
    peer.dataChannel?.close();
    peer.pc.close();
    this.hostPeers.delete(connectionId);
    logger.debug('PeerConnection', `Host peer removed, ${this.hostPeers.size} peers remaining`);
  }

  // ── Signaling ──────────────────────────────────────────────────────────

  private async handleSignalingMessage(message: { type: string; payload: unknown }): Promise<void> {
    switch (message.type) {
      case 'connection_accepted': {
        const { connectionId } = message.payload as { connectionId: string };
        logger.debug('PeerConnection', 'Connection accepted, connectionId:', connectionId);
        this.connectionId = connectionId;

        // Create and send offer
        if (this.pc && this.isInitiator) {
          logger.debug('PeerConnection', 'Creating RTC offer...');
          const offer = await this.pc.createOffer();
          await this.pc.setLocalDescription(offer);
          logger.debug('PeerConnection', 'Sending RTC offer to:', this.targetMachineId);

          serverConnection.send({
            type: 'rtc_offer',
            payload: {
              connectionId,
              targetMachineId: this.targetMachineId,
              sdp: offer.sdp,
            },
          });
        }
        break;
      }

      case 'rtc_offer': {
        const { connectionId, sdp } = message.payload as { connectionId: string; targetMachineId: string; sdp: string };
        // Route to the correct host peer by connectionId
        const hostPeer = this.hostPeers.get(connectionId);
        if (hostPeer) {
          logger.debug('PeerConnection', `Received RTC offer for host peer ${connectionId}`);
          await hostPeer.pc.setRemoteDescription({ type: 'offer', sdp });
          const answer = await hostPeer.pc.createAnswer();
          await hostPeer.pc.setLocalDescription(answer);
          logger.debug('PeerConnection', `Sending RTC answer for host peer ${connectionId}`);

          serverConnection.send({
            type: 'rtc_answer',
            payload: {
              connectionId,
              targetMachineId: hostPeer.targetMachineId,
              sdp: answer.sdp,
            },
          });
        } else if (this.pc && this.isInitiator) {
          // Renegotiation: host sent a new offer on existing connection (e.g. added video track)
          logger.info('PeerConnection', 'Received renegotiation offer from host');
          await this.pc.setRemoteDescription({ type: 'offer', sdp });
          const answer = await this.pc.createAnswer();
          await this.pc.setLocalDescription(answer);

          serverConnection.send({
            type: 'rtc_answer',
            payload: {
              connectionId,
              targetMachineId: this.targetMachineId,
              sdp: answer.sdp,
            },
          });
        } else if (this.pc && !this.isInitiator) {
          // Legacy single-peer path
          logger.debug('PeerConnection', 'Received RTC offer (legacy single-peer)');
          await this.pc.setRemoteDescription({ type: 'offer', sdp });
          const answer = await this.pc.createAnswer();
          await this.pc.setLocalDescription(answer);

          serverConnection.send({
            type: 'rtc_answer',
            payload: {
              connectionId,
              targetMachineId: this.targetMachineId,
              sdp: answer.sdp,
            },
          });
        }
        break;
      }

      case 'rtc_answer': {
        const { connectionId: answerConnId, sdp } = message.payload as { connectionId?: string; sdp: string };
        // Route answer to host peer if connectionId matches (renegotiation answer)
        const answerHostPeer = answerConnId ? this.hostPeers.get(answerConnId) : undefined;
        if (answerHostPeer) {
          logger.debug('PeerConnection', `Received renegotiation answer for host peer ${answerConnId}`);
          await answerHostPeer.pc.setRemoteDescription({ type: 'answer', sdp });
        } else if (this.pc && this.isInitiator) {
          logger.debug('PeerConnection', 'Received RTC answer');
          await this.pc.setRemoteDescription({ type: 'answer', sdp });
          logger.debug('PeerConnection', 'Remote description set, waiting for ICE...');
        }
        break;
      }

      case 'rtc_ice_candidate': {
        const { connectionId: iceConnId, candidate, sdpMid, sdpMLineIndex } = message.payload as {
          connectionId?: string;
          candidate: string;
          sdpMid?: string;
          sdpMLineIndex?: number;
        };
        const iceCandidate = new RTCIceCandidate({
          candidate,
          sdpMid: sdpMid || undefined,
          sdpMLineIndex: sdpMLineIndex ?? undefined,
        });

        // Route to host peer if connectionId matches
        const iceHostPeer = iceConnId ? this.hostPeers.get(iceConnId) : undefined;
        if (iceHostPeer) {
          await iceHostPeer.pc.addIceCandidate(iceCandidate);
        } else if (this.pc) {
          // Fall back to client-mode peer connection
          await this.pc.addIceCandidate(iceCandidate);
        }
        break;
      }

      case 'connection_rejected': {
        logger.debug('PeerConnection', 'Connection rejected');
        this.cleanup();
        break;
      }

      case 'error': {
        // Handle server errors during connection (e.g., CONNECTION_TIMEOUT)
        const payload = message.payload as { code?: string; message?: string; connectionId?: string };
        if (payload.code === 'CONNECTION_TIMEOUT') {
          if (payload.connectionId && this.hostPeers.has(payload.connectionId)) {
            // Host side: clean up the peer created for this abandoned connection
            logger.warn('PeerConnection', `Connection timeout for host peer ${payload.connectionId}`);
            this.cleanupHostPeer(payload.connectionId);
          } else {
            // Client side: clean up our connection attempt
            logger.warn('PeerConnection', 'Connection request timed out on server');
            this.cleanup(); // Forces connect() to resolve(false) via the interval check
          }
        }
        break;
      }
    }
  }

  /**
   * Handle unexpected disconnection — clean up and schedule reconnect
   */
  private handleUnexpectedDisconnect(): void {
    const machineId = this.lastConnectedMachineId;
    this.cleanup();

    // Schedule auto-reconnect if this wasn't intentional
    if (!this.intentionalDisconnect && machineId && this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = Math.min(
        this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
        this.maxReconnectDelay
      );
      this.reconnectAttempts++;
      logger.debug('PeerConnection', `Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms to ${machineId}`);

      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.attemptReconnect(machineId);
      }, delay);
    }
  }

  /**
   * Attempt to reconnect to the last connected machine
   */
  private async attemptReconnect(machineId: string): Promise<void> {
    if (this.pc || this.intentionalDisconnect) return; // Already connected or user disconnected

    logger.debug('PeerConnection', `Attempting reconnect to ${machineId} (attempt ${this.reconnectAttempts})`);

    useMachineStore.getState().setWebrtcConnecting(true);
    try {
      const connected = await this.connect(machineId);
      if (connected) {
        logger.debug('PeerConnection', 'Reconnect successful');
        // Re-switch to WebRTC backend and reload projects
        const { switchToWebRTCBackend } = await import('./backend');
        const { useProjectStore } = await import('../stores/projectStore');
        switchToWebRTCBackend();
        await useProjectStore.getState().loadProjects();
        useMachineStore.getState().setWebrtcReady(true);
        // Same signal CefBackend fires on IPC re-establish: mounted terminal
        // tabs re-sync their session list against the daemon. Without it a
        // reconnected remote client keeps stale tabs (and pre-0.4.26 builds
        // auto-spawned duplicates from the stale state).
        window.dispatchEvent(new Event('ac:core-reconnected'));
      } else {
        logger.warn('PeerConnection', 'Reconnect failed, will retry if attempts remain');
        // handleUnexpectedDisconnect is called from cleanup path if connect fails
      }
    } catch (error) {
      logger.error('PeerConnection', 'Reconnect error:', error);
    } finally {
      useMachineStore.getState().setWebrtcConnecting(false);
    }
  }

  // ── Public accessors for WebSocket→PeerConnection coordination ──────

  get lastTargetMachineId(): string | null {
    return this.lastConnectedMachineId;
  }

  get wasIntentionalDisconnect(): boolean {
    return this.intentionalDisconnect;
  }

  /**
   * Trigger a fresh reconnect attempt (called when WebSocket recovers)
   */
  triggerReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    if (this.lastConnectedMachineId) {
      this.attemptReconnect(this.lastConnectedMachineId);
    }
  }

  // ── ICE restart (lightweight recovery before full teardown) ─────────

  private async performIceRestart(): Promise<void> {
    if (!this.pc || !this.connectionId || !this.targetMachineId) {
      logger.warn('PeerConnection', 'Cannot ICE restart: no active connection');
      this.handleUnexpectedDisconnect();
      return;
    }

    this.iceRestartAttempts++;
    logger.info('PeerConnection', `ICE restart attempt ${this.iceRestartAttempts}/${this.maxIceRestartAttempts}`);

    try {
      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);

      serverConnection.send({
        type: 'rtc_offer',
        payload: {
          connectionId: this.connectionId,
          targetMachineId: this.targetMachineId,
          sdp: offer.sdp,
        },
      });
    } catch (error) {
      logger.error('PeerConnection', 'ICE restart failed:', error);
      this.handleUnexpectedDisconnect();
    }
  }

  private clearIceDisconnectedTimer(): void {
    if (this.iceDisconnectedTimer) {
      clearTimeout(this.iceDisconnectedTimer);
      this.iceDisconnectedTimer = null;
    }
  }

  private async performHostPeerIceRestart(peer: HostPeer): Promise<void> {
    peer.iceRestartAttempts++;
    logger.info('PeerConnection', `Host peer ${peer.connectionId} ICE restart attempt ${peer.iceRestartAttempts}/${this.maxIceRestartAttempts}`);

    try {
      const offer = await peer.pc.createOffer({ iceRestart: true });
      await peer.pc.setLocalDescription(offer);

      serverConnection.send({
        type: 'rtc_offer',
        payload: {
          connectionId: peer.connectionId,
          targetMachineId: peer.targetMachineId,
          sdp: offer.sdp,
        },
      });
    } catch (error) {
      logger.error('PeerConnection', `Host peer ${peer.connectionId} ICE restart failed:`, error);
      this.cleanupHostPeer(peer.connectionId);
    }
  }

  // ── Data channel keepalive ─────────────────────────────────────────

  private startKeepalive(): void {
    this.stopKeepalive();
    this.keepaliveInterval = setInterval(() => {
      if (this.dataChannel?.readyState === 'open') {
        try {
          this.dataChannel.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
          this.keepaliveTimeout = setTimeout(() => {
            logger.warn('PeerConnection', 'Keepalive timeout — no pong in 10s');
            this.keepaliveTimeout = null;
            // Try ICE restart first, not full teardown
            if (this.iceRestartAttempts < this.maxIceRestartAttempts) {
              this.performIceRestart();
            } else {
              this.handleUnexpectedDisconnect();
            }
          }, this.keepaliveTimeoutMs);
        } catch {
          // Channel already closed
        }
      }
    }, this.keepaliveIntervalMs);
  }

  private stopKeepalive(): void {
    if (this.keepaliveInterval) { clearInterval(this.keepaliveInterval); this.keepaliveInterval = null; }
    if (this.keepaliveTimeout) { clearTimeout(this.keepaliveTimeout); this.keepaliveTimeout = null; }
  }

  /**
   * Initialize visibility-based reconnection (call once at startup)
   */
  initVisibilityReconnect(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this.pc && this.lastConnectedMachineId && !this.intentionalDisconnect) {
        logger.debug('PeerConnection', 'App became visible, checking WebRTC connection...');
        // Reset attempts on visibility change to give a fresh set of retries
        this.reconnectAttempts = 0;
        this.attemptReconnect(this.lastConnectedMachineId);
      }
    });
  }

  /**
   * Listen for network type changes (WiFi→cellular etc.) and proactively ICE restart.
   * navigator.connection is Chrome/Edge only — other browsers rely on the ICE
   * disconnected timer from setupPeerConnectionHandlers().
   */
  initNetworkChangeListener(): void {
    const conn = (navigator as unknown as { connection?: { addEventListener: (type: string, fn: () => void) => void; effectiveType?: string } }).connection;
    if (conn) {
      conn.addEventListener('change', () => {
        logger.debug('PeerConnection', `Network change detected: ${conn.effectiveType}`);
        if (this.pc && (this.pc.iceConnectionState === 'connected' || this.pc.iceConnectionState === 'completed')) {
          logger.info('PeerConnection', 'Network changed while connected, proactive ICE restart');
          this.performIceRestart();
        }
      });
    }
  }

  private cleanup(): void {
    const wasClientConnected = this.dataChannel?.readyState === 'open';
    const hadHostPeers = this.hostPeers.size > 0;
    const wasConnected = wasClientConnected || hadHostPeers;

    // Stop keepalive and ICE timers
    this.stopKeepalive();
    this.clearIceDisconnectedTimer();
    this.iceRestartAttempts = 0;

    // Clean up client-mode connection
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }
    this.connectionId = null;
    this.targetMachineId = null;
    this.pendingRequests.clear();
    this.pendingChunks.clear();
    // Note: Do NOT clear remoteTrackHandlers or rdpInputHandlers here.
    // They are component subscriptions that must survive reconnection.
    // Cleanup happens when components unmount via the unsubscribe functions.

    // Clean up rdp-input channel
    if (this.rdpInputChannel) {
      this.rdpInputChannel.close();
      this.rdpInputChannel = null;
    }

    // Clean up all host peers (including their timers)
    for (const [, peer] of this.hostPeers) {
      if (peer.iceDisconnectedTimer) clearTimeout(peer.iceDisconnectedTimer);
      peer.dataChannel?.close();
      peer.pc.close();
    }
    this.hostPeers.clear();
    this.hostSignalingSubscribed = false;
    if (this.hostPeerCleanupInterval) {
      clearInterval(this.hostPeerCleanupInterval);
      this.hostPeerCleanupInterval = null;
    }

    // Reset webrtcReady when peer connection is cleaned up
    if (wasConnected) {
      useMachineStore.getState().setWebrtcReady(false);
    }

    // Notify disconnect handlers if we were previously connected
    if (wasConnected) {
      logger.debug('PeerConnection', 'Notifying disconnect handlers');
      for (const handler of this.disconnectHandlers) {
        try {
          handler();
        } catch (error) {
          logger.error('PeerConnection', 'Error in disconnect handler:', error);
        }
      }
    }
  }
}

// Singleton instance
export const peerConnection = new PeerConnection();

// Initialize visibility-based reconnection and network change detection
peerConnection.initVisibilityReconnect();
peerConnection.initNetworkChangeListener();
